import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { Plant, ChartBar, Users, CurrencyInr, SignOut, UserCircle, ChartLineUp, Receipt, UserGear, Buildings, Wallet, Headset, Archive } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isOwner, canManageCompanies, hasPerm, logout } = useAuth();

  const navItems = useMemo(() => [
    { path: '/', icon: ChartBar, label: 'Dashboard', testId: 'nav-dashboard', show: true },
    { path: '/tracker', icon: Users, label: 'Tracker', testId: 'nav-tracker', show: hasPerm('tracker') },
    { path: '/daily', icon: Headset, label: 'Daily Follow-up', testId: 'nav-daily', show: hasPerm('daily') },
    { path: '/archive', icon: Archive, label: 'Daily Archive', testId: 'nav-archive', show: hasPerm('archive') },
    { path: '/finance', icon: CurrencyInr, label: 'Finance', testId: 'nav-finance', show: hasPerm('finance') },
    { path: '/billing', icon: Receipt, label: 'Billing', testId: 'nav-billing', show: hasPerm('billing') },
    { path: '/reports', icon: ChartLineUp, label: 'Reports', testId: 'nav-reports', show: hasPerm('reports') },
    { path: '/salary', icon: Wallet, label: 'Salary', testId: 'nav-salary', show: hasPerm('salary') },
    { path: '/users', icon: UserGear, label: 'Users', testId: 'nav-users', show: isOwner },
    { path: '/companies', icon: Buildings, label: 'Companies', testId: 'nav-companies', show: canManageCompanies },
  ].filter(i => i.show), [isOwner, canManageCompanies, hasPerm]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-64 border-r-2 border-[#A3E635] bg-[#0B2818] min-h-screen flex flex-col">
        <div className="p-6 border-b-2 border-[#A3E635]">
          <div className="flex items-center gap-3">
            <Plant size={40} weight="duotone" className="text-[#A3E635]" />
            <div>
              <h1 className="text-2xl font-black tracking-tight text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Tulsi Jobs</h1>
              <p className="text-xs text-white font-medium">Placement CRM</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <button
                    data-testid={item.testId}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-md border-2 font-bold text-sm transition-all ${
                      isActive
                        ? 'bg-[#A3E635] text-black border-[#A3E635] shadow-[2px_2px_0px_0px_rgba(163,230,53,0.5)]'
                        : 'bg-[#0F3622] text-white border-[#143E29] hover:bg-[#143E29] hover:text-white hover:shadow-[2px_2px_0px_0px_rgba(163,230,53,0.3)]'
                    }`}
                  >
                    <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t-2 border-[#A3E635] space-y-3">
          <div data-testid="current-user" className="flex items-center gap-2 px-3 py-2 bg-[#0F3622] border-2 border-[#143E29] rounded-md">
            <UserCircle size={24} weight="fill" className={isOwner ? "text-[#FDE047]" : "text-[#93C5FD]"} />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-white truncate">
                {isOwner ? 'Owner' : 'User'}
              </p>
              <p className="text-xs text-white truncate" title={user?.email}>{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#F87171] text-black border-2 border-[#F87171] rounded-md font-bold text-sm hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(248,113,113,0.5)] transition-all"
          >
            <SignOut size={16} weight="bold" />
            Logout
          </button>
          <p className="text-xs text-white text-center font-medium">© 2026 Tulsi Jobs</p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 bg-[#0B2818] p-4 sm:p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
